import { queryOptions, useQuery } from '@tanstack/react-query'

import { orderKeys } from './query-key-factory'

import { getUser } from '@/lib/auth'
import { QueryConfig } from '@/lib/react-query'
import { supabase } from '@/lib/supabase-client'
import { logger } from '@/logger'
import { OrderStatus } from '@/types/order'
import { calculatePaginationRange } from '@/utils/db'

export type ListOrder = Awaited<ReturnType<typeof getOrders>>['data'][0]
export type FilterStatus = 'all' | OrderStatus
export type Filter = {
  field: 'order_number' | 'practician.display_name'
  value?: string
}

export type GetOrdersOptions = {
  status?: FilterStatus
  filters?: Filter[]
  page?: number
  itemsPerPage?: number
}
export const getOrders = async ({ status = 'all', filters, page = 1, itemsPerPage = 10 }: GetOrdersOptions) => {
  const { data: userData } = await getUser()
  if (!userData || !userData.user) {
    logger.error('getOrders: User data not found')
    throw 'No user ID provided'
  }
  const query = supabase.from('orders').select(
    `*,
      service:services(*),
      technician:users!orders_technician_id_fkey(*),
      practician:users!orders_practician_id_fkey(*),
      patient:patients(*),
      status_history:order_status_history(*),
      messages:order_messages(*),
      attachments:order_attachments(*)`,
    {
      count: 'exact',
    },
  )

  const uid = userData.user.id
  const userProfile = userData.user.user_metadata.profile
  if (userProfile === 'practician') {
    query.eq('practician_id', uid)
  } else if (userProfile === 'technician') {
    query.eq('technician_id', uid)
  } else {
    logger.error('provided user profile is unknown', { userProfile })
    throw 'provided user profile is unknown'
  }

  if (status !== 'all') {
    query.eq('status', status)
  }

  // FIXME
  if (filters) {
    for (const filter of filters) {
      if (filter.field && filter.value) {
        const value = filter.value.trim()
        if (value) {
          if (filter.field === 'order_number') {
            const numValue = Number(value)
            if (!isNaN(numValue)) {
              query.eq(filter.field, numValue)
            } else {
              query.ilike(filter.field, `%${value}%`)
            }
          } else {
            query.ilike(filter.field, `%${value}%`)
          }
        }
      }
    }
  }

  query.order('created_at', { ascending: false })
  const { start, end } = calculatePaginationRange(page, itemsPerPage)
  query.range(start, end)
  const { data, count, error } = await query

  if (error) {
    logger.error('Error fetching orders', {
      errorMessage: error,
      uid,
      userProfile,
      status,
      page,
      itemsPerPage,
    })
    throw error
  }

  return {
    data,
    meta: { totalPages: Math.ceil((count || 0) / itemsPerPage), page },
  }
}

export const getOrdersQueryOptions = ({ status, page, filters, itemsPerPage }: GetOrdersOptions) => {
  return queryOptions({
    queryKey: orderKeys.list({ status, page, filters, itemsPerPage }),
    queryFn: () => getOrders({ status, page, filters, itemsPerPage }),
  })
}

type UseOrdersOptions = GetOrdersOptions & {
  queryConfig?: QueryConfig<typeof getOrdersQueryOptions>
}

export const useOrders = ({ status = 'all', filters, page = 1, itemsPerPage = 10, queryConfig }: UseOrdersOptions) => {
  return useQuery({
    ...getOrdersQueryOptions({ status, filters, page, itemsPerPage }),
    ...queryConfig,
  })
}
